import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Alert,
    CircularProgress,
    Chip,
    IconButton,
    Typography
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import {
    getReviewDimensions,
    getReviewDefinitions,
    getReviewEntityTypes,
    addNewReviewQuestion,
    getApiErrorMessage,
    ReviewDimension,
    ReviewDefinition,
    ReviewEntityType
} from '../../services/api';

interface AddQuestionDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    defaultReviewDefinitionId?: number;
    defaultReviewEntityTypeId?: number;
    reviewDefinitionName?: string;
    reviewEntityTypeName?: string;
    reviewEntityTypeDisplayLabel?: string;
}

const ANSWER_TYPES = [
    'YES-NO',
    'NUMERIC',
    'PERCENTAGE',
    'TEXT',
    'SELECT',
    'MULTI-SELECT'
];

const AddQuestionDialog: React.FC<AddQuestionDialogProps> = ({
    open,
    onClose,
    onSuccess,
    defaultReviewDefinitionId,
    defaultReviewEntityTypeId,
    reviewDefinitionName,
    reviewEntityTypeName,
    reviewEntityTypeDisplayLabel
}) => {
    const [reviewQuestionText, setReviewQuestionText] = useState('');
    const [description, setDescription] = useState('');
    const [reviewDimensionId, setReviewDimensionId] = useState<number | ''>('');
    const [reviewDefinitionId, setReviewDefinitionId] = useState<number | ''>(defaultReviewDefinitionId || '');
    const [reviewEntityTypeId, setReviewEntityTypeId] = useState<number | ''>(defaultReviewEntityTypeId || '');
    const [answerType, setAnswerType] = useState<string>('');
    const [answerOptions, setAnswerOptions] = useState<string[]>([]);
    const [newOption, setNewOption] = useState('');
    const [defaultValue, setDefaultValue] = useState('');
    const [idealValue, setIdealValue] = useState('');
    const [actionRequired, setActionRequired] = useState('');
    
    const [dimensions, setDimensions] = useState<ReviewDimension[]>([]);
    const [definitions, setDefinitions] = useState<ReviewDefinition[]>([]);
    const [entityTypes, setEntityTypes] = useState<ReviewEntityType[]>([]);
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fetchingData, setFetchingData] = useState(true);

    useEffect(() => {
        if (open) {
            fetchDropdownData();
        }
    }, [open]);

    const fetchDropdownData = async () => {
        setFetchingData(true);
        try {
            const [dimensionsRes, definitionsRes, entityTypesRes] = await Promise.all([
                getReviewDimensions(),
                getReviewDefinitions(),
                getReviewEntityTypes()
            ]);
            
            setDefinitions(definitionsRes.data);
            setEntityTypes(entityTypesRes.data);
            
            // Filter dimensions based on the current review entity type's associated_review_dimensions
            if (defaultReviewEntityTypeId) {
                const currentEntityType = entityTypesRes.data.find(
                    (et: ReviewEntityType) => et.id === defaultReviewEntityTypeId
                );
                
                if (currentEntityType?.associated_review_dimensions && currentEntityType.associated_review_dimensions.length > 0) {
                    const filteredDimensions = dimensionsRes.data.filter(
                        (dim: ReviewDimension) => currentEntityType.associated_review_dimensions!.includes(dim.id)
                    );
                    setDimensions(filteredDimensions);
                } else {
                    setDimensions(dimensionsRes.data);
                }
            } else {
                setDimensions(dimensionsRes.data);
            }
            
            setFetchingData(false);
        } catch (err) {
            setError('Failed to load dropdown data');
            setFetchingData(false);
        }
    };

    const handleAddOption = () => {
        if (newOption.trim()) {
            setAnswerOptions(prev => [...prev, newOption.trim()]);
            setNewOption('');
        }
    };

    const handleRemoveOption = (index: number) => {
        setAnswerOptions(prev => prev.filter((_, i) => i !== index));
    };

    const handleAnswerTypeChange = (value: string) => {
        setAnswerType(value);
        // Set answer options for YES-NO, clear for non-SELECT types
        if (value === 'YES-NO') {
            setAnswerOptions(['Yes', 'No']);
        } else if (!['SELECT', 'MULTI-SELECT'].includes(value)) {
            setAnswerOptions([]);
        }
        // Reset default and ideal values when changing answer type
        setDefaultValue('');
        setIdealValue('');
    };

    const handleSubmit = async () => {
        if (!reviewQuestionText.trim()) {
            setError('Question text is required');
            return;
        }
        if (!reviewDimensionId) {
            setError('Review dimension is required');
            return;
        }
        if (!reviewDefinitionId) {
            setError('Review definition is required');
            return;
        }
        if (!reviewEntityTypeId) {
            setError('Review entity type is required');
            return;
        }
        if (!answerType) {
            setError('Answer type is required');
            return;
        }
        if (['SELECT', 'MULTI-SELECT'].includes(answerType) && answerOptions.length === 0) {
            setError('At least one answer option is required for SELECT/MULTI-SELECT');
            return;
        }
        if (!reviewEntityTypeName) {
            setError('Review entity type name is required');
            return;
        }
        if (!reviewDefinitionName) {
            setError('Review definition name is required');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const questionData: any = {
                review_question_text: reviewQuestionText,
                description: description,
                review_dimension_id: reviewDimensionId as number,
                review_definition_id: reviewDefinitionId as number,
                review_entity_type_id: reviewEntityTypeId as number,
                answer_type: answerType,
                review_question_type: 'CUSTOM',
                review_entity_type: reviewEntityTypeName,
                default_value: defaultValue || undefined,
                ideal_value: idealValue || undefined,
                action_required: actionRequired || undefined
            };

            // Add answer_options for SELECT, MULTI-SELECT, and YES-NO
            if (['SELECT', 'MULTI-SELECT', 'YES-NO'].includes(answerType)) {
                questionData.answer_options = answerOptions;
            }

            await addNewReviewQuestion(questionData);

            handleClose();
            onSuccess();
        } catch (err: any) {
            setError(getApiErrorMessage(err, 'Failed to create question'));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setReviewQuestionText('');
        setDescription('');
        setReviewDimensionId('');
        setReviewDefinitionId(defaultReviewDefinitionId || '');
        setReviewEntityTypeId(defaultReviewEntityTypeId || '');
        setAnswerType('');
        setAnswerOptions([]);
        setNewOption('');
        setDefaultValue('');
        setIdealValue('');
        setActionRequired('');
        setError('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Add New Question</DialogTitle>
            <DialogContent>
                {fetchingData ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        {/* Review Definition and Entity Type - Read-only labels at the top */}
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                gap: 2, 
                                p: 2, 
                                backgroundColor: '#e3f2fd',
                                borderRadius: 1,
                                border: '1px solid #90caf9'
                            }}
                        >
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Review Definition
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                    {reviewDefinitionName || '-'}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Review Entity Type
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                                    {reviewEntityTypeDisplayLabel || reviewEntityTypeName || '-'}
                                </Typography>
                            </Box>
                        </Box>

                        {error && (
                            <Alert severity="error" onClose={() => setError('')}>
                                {error}
                            </Alert>
                        )}

                        <TextField
                            label="Question Text"
                            value={reviewQuestionText}
                            onChange={(e) => setReviewQuestionText(e.target.value)}
                            fullWidth
                            required
                            multiline
                            rows={2}
                        />

                        <TextField
                            label="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                        />

                        <FormControl fullWidth required>
                            <InputLabel>Review Dimension</InputLabel>
                            <Select
                                value={reviewDimensionId}
                                onChange={(e) => setReviewDimensionId(e.target.value as number)}
                                label="Review Dimension"
                            >
                                {dimensions.map((dimension) => (
                                    <MenuItem key={dimension.id} value={dimension.id}>
                                        {dimension.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth required>
                            <InputLabel>Answer Type</InputLabel>
                            <Select
                                value={answerType}
                                onChange={(e) => handleAnswerTypeChange(e.target.value)}
                                label="Answer Type"
                            >
                                {ANSWER_TYPES.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {['SELECT', 'MULTI-SELECT'].includes(answerType) && (
                            <Box>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                    <TextField
                                        label="Add Answer Option"
                                        value={newOption}
                                        onChange={(e) => setNewOption(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddOption();
                                            }
                                        }}
                                        fullWidth
                                        size="small"
                                    />
                                    <IconButton
                                        color="primary"
                                        onClick={handleAddOption}
                                        disabled={!newOption.trim()}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: '40px' }}>
                                    {answerOptions.map((option, index) => (
                                        <Chip
                                            key={index}
                                            label={option}
                                            onDelete={() => handleRemoveOption(index)}
                                            deleteIcon={<CloseIcon />}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                                {answerOptions.length === 0 && (
                                    <Box sx={{ mt: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
                                        Add at least one answer option
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Display YES-NO options as read-only chips */}
                        {answerType === 'YES-NO' && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                    Answer Options:
                                </Typography>
                                {answerOptions.map((option, index) => (
                                    <Chip
                                        key={index}
                                        label={option}
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                ))}
                            </Box>
                        )}

                        {/* Default Value Field */}
                        {answerType === 'YES-NO' ? (
                            <FormControl fullWidth>
                                <InputLabel>Default Value</InputLabel>
                                <Select
                                    value={defaultValue}
                                    onChange={(e) => setDefaultValue(e.target.value)}
                                    label="Default Value"
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    <MenuItem value="Yes">Yes</MenuItem>
                                    <MenuItem value="No">No</MenuItem>
                                </Select>
                            </FormControl>
                        ) : ['SELECT', 'MULTI-SELECT'].includes(answerType) ? (
                            <FormControl fullWidth>
                                <InputLabel>Default Value</InputLabel>
                                <Select
                                    value={defaultValue}
                                    onChange={(e) => setDefaultValue(e.target.value)}
                                    label="Default Value"
                                    disabled={answerOptions.length === 0}
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {answerOptions.map((option, index) => (
                                        <MenuItem key={index} value={option}>{option}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : answerType ? (
                            <TextField
                                label="Default Value"
                                value={defaultValue}
                                onChange={(e) => setDefaultValue(e.target.value)}
                                fullWidth
                                helperText="Suggested starting value for this question"
                            />
                        ) : null}

                        {/* Ideal Value Field */}
                        {answerType === 'YES-NO' ? (
                            <FormControl fullWidth>
                                <InputLabel>Ideal Value</InputLabel>
                                <Select
                                    value={idealValue}
                                    onChange={(e) => setIdealValue(e.target.value)}
                                    label="Ideal Value"
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    <MenuItem value="Yes">Yes</MenuItem>
                                    <MenuItem value="No">No</MenuItem>
                                </Select>
                            </FormControl>
                        ) : ['SELECT', 'MULTI-SELECT'].includes(answerType) ? (
                            <FormControl fullWidth>
                                <InputLabel>Ideal Value</InputLabel>
                                <Select
                                    multiple
                                    value={idealValue ? idealValue.split(',').map(v => v.trim()).filter(Boolean) : []}
                                    onChange={(e) => setIdealValue((e.target.value as unknown as string[]).join(','))}
                                    label="Ideal Value"
                                    disabled={answerOptions.length === 0}
                                    renderValue={(selected) => (selected as string[]).join(', ')}
                                >
                                    {answerOptions.map((option, index) => (
                                        <MenuItem key={index} value={option}>{option}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : answerType ? (
                            <TextField
                                label="Ideal Value"
                                value={idealValue}
                                onChange={(e) => setIdealValue(e.target.value)}
                                fullWidth
                                helperText="Target or optimal value for this question"
                            />
                        ) : null}

                        {/* Action Required Field */}
                        {answerType && (
                            <TextField
                                label="Action Required"
                                value={actionRequired}
                                onChange={(e) => setActionRequired(e.target.value)}
                                fullWidth
                                multiline
                                rows={2}
                                helperText="Action to take when answer doesn't match ideal value"
                            />
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || fetchingData}
                >
                    {loading ? <CircularProgress size={24} /> : 'Add Question'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddQuestionDialog;
